import moment from 'moment'
import prop from 'vue-strict-prop'
import { componentFactoryOf, modifiers as m } from 'vue-tsx-support'

import Button from '@/components/semantic-ui/Button'

import { Coupon, ShoppingListItem } from '@/datastore/models'

import auth from '@/store/modules/auth'
import grocery from '@/store/modules/grocery'

interface Events {
	onAddToCart(): void
	onRemove(): void
}

export default componentFactoryOf<Events>().create({
	name: 'ShoppingListItem',
	props: {
		item: prop.ofType<ShoppingListItem>().required,
	},
	computed: {
		addedDetail(): string | null {
			const { createdAt, createdBy } = this.item

			if (!createdAt && !createdBy) {
				return null
			}

			let detail = 'Added'

			if (createdBy) {
				const displayName = (createdBy === this.user?.uid)
					? 'you'
					: Object.values(grocery.households_usersCollection)
						.find(u => u.userId === createdBy)?.displayName ?? 'someone'
				detail += ` by ${displayName}`
			}

			if (createdAt) {
				detail += ` ${moment(createdAt).fromNow()}`
			}

			return detail
		},
		coupons(): Coupon[] {
			return grocery.coupons
				.filter(coupon => coupon.promoItems.some(item => item.pantryItemId === this.item.pantryItemId))
		},
		pantryItemNotes(): string {
			return this.item.pantryItemId
				? grocery.pantryItemsCollection[this.item.pantryItemId].notes
				: ''
		},
		preferredBrandList(): string {
			return (this.item.preferredBrands ?? [])
				.map(brandId => grocery.brandsCollection[brandId])
				.filter(brand => !!brand)
				.map(brand => brand.name)
				.join(', ')
		},
		quantity(): number {
			return this.item.quantity ?? 1
		},
		user(): firebase.UserInfo | null {
			return auth.user
		},
	},
	methods: {
		incrementQuantity(amount: number) {
			const { $id: id } = this.item
			grocery.incrementShoppingListItemQuantity({ amount, id })
		},
	},
	render() {
		return (
			<div class="ui fluid card">
				<div class="content">
					<div class="right floated meta">
						<Button
							class="mini circular basic icon"
							disabled={this.quantity < 2}
							onClick={() => this.incrementQuantity(-1)}
							>
							<i class="minus icon" />
						</Button>
						<Button class="mini circular basic icon" onClick={() => this.incrementQuantity(1)}>
							<i class="plus icon" />
						</Button>
					</div>
					<div class="header">
						<router-link to={this.item.pantryItemId ? `/item/${this.item.pantryItemId}` : ''}>
							{this.item.name}
						</router-link>
						&nbsp;<small>x</small>{this.quantity}
					</div>
					{!!this.addedDetail &&
						<div class="meta">
							{this.addedDetail}
						</div>
					}
					<div class="description">
						{!!this.pantryItemNotes &&
							<div>
								{this.pantryItemNotes}
							</div>
						}
					</div>
				</div>
				{!!(this.item.notes || this.preferredBrandList) &&
					<div class="extra content">
						{!!this.item.notes &&
							<div>
								<i class="comment icon" />{this.item.notes}
							</div>
						}
						{!!this.preferredBrandList &&
							<div>
								<i class="tag icon" />{this.preferredBrandList}
							</div>
						}
					</div>
				}
				{this.coupons.length > 0 &&
					<div class="extra content">
						<div class="ui middle aligned relaxed divided animated list">
							{this.coupons.map(coupon =>
								<div class="item" onClick={m.stop(() => this.$router.push(`/coupon/${coupon.$id}`))}>
									<i class="large money check alternate middle aligned icon" />
									<div class="content">
										<a class="header">
											{coupon.title}
										</a>
										<div class="description">
											{coupon.description}
										</div>
										{coupon.expirationDate &&
											<div v-if="coupon.expirationDate" class="right floated">
												Expires {new Date(coupon.expirationDate).toLocaleDateString()}
											</div>
										}
									</div>
								</div>)
							}
						</div>
					</div>
				}
				<div class="extra content">
					<div class="ui two buttons">
						<Button class="basic fluid positive" onClick={() => this.$emit('addToCart')}>
							<i class="cart arrow down icon" />
							Add to Cart
						</Button>
						<Button class="basic fluid negative" onClick={() => this.$emit('remove')}>
							<i class="trash icon" />
							Remove
						</Button>
					</div>
				</div>
			</div>
		)
	},
})
